/**
 * The console's one presentation mapping from the ids the API sends to the words a
 * person reads (Cortex Console Decisions and Review blades package, harness row CC2-11;
 * requirements.md FR-14; display-label ruling DP-CC-03).
 *
 * The persisted role id `founder` is read as "Operator" wherever a person meets it: who
 * a decision is for, who ruled it, and any id a blade shows as words (a column, a state,
 * a liveness) that carries the role id inside it. Nothing persisted changes. The API
 * still sends `founder`, grants and tokens still hold it, and nothing here is sent back
 * to the server.
 *
 * STATIC_RUNTIME_LOGIC: none (spec.md 10C). One presentation constant keyed on the role
 * catalogue's own id. It reads no free text and decides nothing.
 */

/** The persisted role ids a person reads under another name. */
export const ROLE_DISPLAY_LABELS: Readonly<Record<string, string>> = {
  founder: "Operator",
};

/** A persisted role id as a person reads it. An id with no display label is shown as sent. */
export function roleLabel(roleId: string): string {
  return ROLE_DISPLAY_LABELS[roleId] ?? roleId;
}

/**
 * An id the API sends, in words: the blade's own English for it where the blade has
 * some, otherwise the id with its underscores as spaces and a capital first letter, so
 * an id this SPA has never met is still shown rather than dropped. A role id inside the
 * id is read as its display label.
 */
export function idLabel(id: string, labels: Readonly<Record<string, string>> = {}): string {
  if (labels[id]) {
    return labels[id];
  }
  const words = id
    .split("_")
    .filter(Boolean)
    .map((word) => ROLE_DISPLAY_LABELS[word] ?? word)
    .join(" ");
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}
